/**
 * GA4 + Meta Pixel tracking helper.
 * Spec: admin.teado.ai/#events  (surface: "teado.ai (landing)")
 *
 * One call fires both GA4 and Meta with a shared event_id so a later
 * server-side CAPI call can dedupe against the browser event.
 * Safe to call anywhere: no-ops when the tags are absent (SSR, static
 * prerender, blocked by an ad blocker, or IDs not configured).
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
  }
}

/** Events emitted by the landing surface. */
export type EventName =
  | "view_section"
  | "cta_click"
  | "language_switch"
  | "contact_click"
  | "external_link_to_teacher";

/** Internal name -> Meta standard event. Unmapped events go to GA4 only. */
const META_MAP: Partial<Record<EventName, string>> = {
  // The landing page is top-of-funnel: nothing here is a Meta standard
  // conversion. Cross-domain clicks to the app are the strongest signal,
  // so they go over as Lead for retargeting audiences.
  external_link_to_teacher: "Lead",
};

function eventId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function track(
  name: EventName,
  params: Record<string, unknown> = {}
): void {
  if (typeof window === "undefined") return;

  const event_id = eventId();

  window.gtag?.("event", name, { ...params, event_id });

  const metaName = META_MAP[name];
  if (metaName) {
    window.fbq?.("track", metaName, params, { eventID: event_id });
  }
}

/**
 * Track a cross-domain click to teacher.teado.ai, then open it.
 * Emits both cta_click and external_link_to_teacher — the first for CTA
 * performance, the second to stitch the session across the domain hop.
 */
export function trackAndOpen(
  url: string,
  opts: {
    ctaLabel: string;
    ctaLocation: string;
    linkType: "signup" | "login" | "demo";
  }
): void {
  track("cta_click", {
    cta_label: opts.ctaLabel,
    cta_location: opts.ctaLocation,
    target_url: url,
  });
  track("external_link_to_teacher", {
    link_type: opts.linkType,
    source_section: opts.ctaLocation,
  });
  window.open(url, "_blank", "noopener,noreferrer");
}
